<script setup>
// =============================================================================
// FangateBoundary.vue
//
// Vue 3 error boundary for the Fangate shell.
//
// Wraps a child column (Sidebar / ListPane / ChatArea / Settings / Panel) so
// that ANY render/setup/lifecycle error inside that subtree is caught here
// and shown as an inline error card — the rest of the Fangate layout stays
// fully interactive.
//
// Usage:
//   <FangateBoundary label="Sidebar">
//     <FangateSidebar v-bind="…" @… />
//   </FangateBoundary>
//
// Why this exists:
//   Without a boundary, one undefined variable in a template or one bad
//   lifecycle hook in any child component crashes the entire /fangate route
//   and the user sees a blank white page with no clue what's broken.
//   onErrorCaptured at this level halts propagation (`return false`) so the
//   parent <FangateLayout> never notices the failure.
// =============================================================================

import { ref, onErrorCaptured } from 'vue';

const props = defineProps({
  /** Human label shown in the error card — e.g. "List Pane", "Chat Area" */
  label:  { type: String, default: 'Component' },
  /**
   * Toggle visual style — 'inline' fills the column area, 'banner' shows
   * a slim top banner. Default 'inline' suits column-shaped subtrees.
   */
  variant: { type: String, default: 'inline' },
});

const err     = ref(null);
const reloads = ref(0);

onErrorCaptured((e, _instance, info) => {
  err.value = {
    message: e?.message ?? String(e),
    stack:   (e?.stack ?? '').split('\n').slice(0, 5).join('\n'),
    info,
    when:    new Date().toLocaleTimeString(),
  };
  // eslint-disable-next-line no-console
  console.error(`[FangateBoundary:${props.label}]`, info, e);
  return false;   // stop propagation — keep the rest of the route alive
});

// Force-remount the wrapped subtree by bumping a key (clears stale state)
const retry = () => {
  err.value = null;
  reloads.value += 1;
};
</script>

<template>
  <!-- Error card replaces the broken subtree entirely -->
  <div v-if="err" class="fbnd-error" :class="'fbnd-error--' + variant">
    <div class="fbnd-icon" aria-hidden="true">
      <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" stroke-width="1.7"
           stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/>
        <line x1="12" y1="8"  x2="12" y2="12"/>
        <line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    </div>
    <div class="fbnd-text">
      <div class="fbnd-title">{{ label }} crashed</div>
      <div class="fbnd-msg" :title="err.message">{{ err.message }}</div>
      <details class="fbnd-details">
        <summary>technical details</summary>
        <div class="fbnd-info">{{ err.info }} · {{ err.when }}</div>
        <pre class="fbnd-stack">{{ err.stack }}</pre>
      </details>
    </div>
    <button class="fbnd-retry" @click="retry">Retry</button>
  </div>

  <!-- Happy path: render the wrapped subtree as-is. Key reloads on retry. -->
  <template v-else>
    <slot :key="reloads" />
  </template>
</template>

<style lang="scss" scoped>
.fbnd-error {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-family: 'Inter', sans-serif;
  &--inline { margin: 14px; }
  &--banner { margin: 0; border-radius: 0; }
}
.fbnd-icon { flex-shrink: 0; color: #b91c1c; padding-top: 1px; }
.fbnd-text { flex: 1; min-width: 0; }
.fbnd-title {
  font-family: 'Libre Baskerville', serif;
  font-size: 14px;
  font-weight: 700;
  color: #7f1d1d;
  margin-bottom: 4px;
}
.fbnd-msg {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: #991b1b;
  word-break: break-word;
  line-height: 1.45;
}
.fbnd-details {
  margin-top: 8px;
  summary {
    cursor: pointer;
    font-size: 11px;
    color: #b91c1c;
    user-select: none;
    &:hover { color: #7f1d1d; }
  }
}
.fbnd-info {
  margin-top: 6px;
  font-size: 11px;
  color: #b91c1c;
}
.fbnd-stack {
  margin: 6px 0 0;
  padding: 8px 10px;
  background: rgba(254, 226, 226, 0.6);
  border-radius: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10.5px;
  line-height: 1.45;
  color: #7f1d1d;
  white-space: pre-wrap;
  overflow-x: auto;
  max-height: 180px;
}
.fbnd-retry {
  flex-shrink: 0;
  padding: 6px 12px;
  border-radius: 7px;
  border: 1px solid #fecaca;
  background: #fff;
  color: #b91c1c;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s;
  &:hover { background: #fef2f2; }
}
</style>
